// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditEventContextAction, UrlLinkList } from '@/audit/api/types';
import { LaunchRounded } from '@/constants/hardfin-standard-icons';
import { createViewableURLString } from '@/utilities/create-viewable-url-string';
import { Link, Stack, Tooltip, Typography } from '@mui/material';

interface URLLinkValue {
  id: string;
  key: string;
  name: string | null;
  url: string;
}

interface URLLinkDifference {
  id: string;
  name: string | null;
  url: string;
  isOld: boolean;
  isNew: boolean;
}

interface Props {
  property: UrlLinkList;
  action: AuditEventContextAction;
}

interface URLLinkDisplayProps {
  urlLink: URLLinkValue;
}

/**
 * Renders a URL link value with name and clickable URL.
 * Shows the name if available, otherwise shows the URL as the display text.
 * Always shows the URL and name side by side if both are present.
 */
function URLLinkDisplay(props: URLLinkDisplayProps) {
  const viewableURL = createViewableURLString(props.urlLink.url);

  if (viewableURL === null) {
    return (
      <Tooltip title="URL is invalid or may be unsafe to link directly" arrow>
        <Stack
          spacing={0.5}
          direction="row"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <Typography
            variant="caption"
            component="span"
            color="error.main"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '20rem',
            }}
          >
            {props.urlLink.url}
          </Typography>
          {props.urlLink.name && (
            <Typography
              variant="caption"
              component="span"
              color="text.secondary"
            >
              ({props.urlLink.name})
            </Typography>
          )}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Link
      href={viewableURL}
      target="_blank"
      rel="noopener"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <Stack spacing={0.5} direction="row">
        <Typography
          variant="caption"
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '20rem',
          }}
        >
          {props.urlLink.url}
        </Typography>
        {props.urlLink.name && (
          <Typography variant="caption" component="span" color="text.secondary">
            ({props.urlLink.name})
          </Typography>
        )}
      </Stack>
      <LaunchRounded sx={{ fontSize: '0.75rem' }} color="action" />
    </Link>
  );
}

/**
 * Component that displays URL link list changes in audit events.
 * Shows added and removed links with proper diff highlighting.
 * Follows the same pattern as AuditFileListLabel for list styling.
 */
export function AuditUrlLinkListLabel(props: Props) {
  const urlLinks: URLLinkDifference[] = [];

  // Process old values (marked as old but not new)
  props.property.oldValue?.forEach(({ id, name, url }) => {
    urlLinks.push({ id, name, url, isOld: true, isNew: false });
  });

  // Process new values (either new additions or updates to existing)
  props.property.newValue?.forEach(({ id, name, url }) => {
    const existingLink = urlLinks.find((link) => link.id === id);

    if (existingLink === undefined) {
      // This is a new link
      return urlLinks.push({ id, name, url, isOld: false, isNew: true });
    }

    // This link existed before, mark it as both old and new (unchanged)
    existingLink.isNew = true;
  });

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {urlLinks.length > 0 ? (
        urlLinks.map((urlLinkDiff) => {
          const { id, name, url, isNew, isOld } = urlLinkDiff;
          const urlLink: URLLinkValue = { id, key: id, name, url };
          // Skip unchanged items (both old and new)
          if (isNew === isOld) {
            return null;
          }

          return (
            <Typography
              key={id}
              variant="caption"
              marginLeft={2}
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <URLLinkDisplay urlLink={urlLink} />
              {props.action !== 'DELETE' && (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ textDecoration: 'underline' }}
                >
                  {isNew ? 'added' : 'removed'}
                </Typography>
              )}
            </Typography>
          );
        })
      ) : (
        <Typography
          component="span"
          variant="caption"
          marginLeft={2}
          color="text.secondary"
        >
          None
        </Typography>
      )}
    </Stack>
  );
}
